import React from 'react';
import { ASSETS_BASE } from '../assets/manifest.js';

interface ItemIconProps {
  readonly icon?: number | null | undefined;
  /**
   * De qual folha de ícones o índice fala.
   *
   * O RPG Maker guarda o ícone como uma casa numa grade — a 176ª de
   * `IconSet.png` —, e cada projeto tem a sua folha, sem nada em comum com a
   * de outro. Com uma folha só, a poção de um jogo importado aparecia com o
   * ícone que estivesse na casa 176 da folha DE CASA: um ícone real, de outro
   * item, que é o pior tipo de erro visual — ninguém percebe que está errado.
   * Ausente, é a folha do acervo original, que é o caso da imensa maioria.
   */
  readonly iconSheet?: string | null | undefined;
  readonly type?: string | undefined;
  /**
   * O lado do ícone. Número é pixel; texto é comprimento CSS.
   *
   * A forma em texto existe para a interface de batalha, cujas janelas são
   * medidas em `em` e crescem junto com o campo — um ícone travado em 24px
   * encolheria sozinho em tela cheia, ao lado de um texto que dobrou.
   */
  readonly size?: number | string | undefined;
  readonly className?: string | undefined;
  readonly title?: string | undefined;
}

const TYPE_FALLBACKS: Record<string, string> = {
  espada: '🗡️',
  lanca: '🔱',
  machado: '🪓',
  arco: '🏹',
  cajado: '🪄',
  adaga: '🗡️',
  malho: '🔨',
  garra: '🐾',
  luva: '🥊',
  besta: '🏹',
  chicote: '〰️',
  arma_de_fogo: '💥',
};

export const ItemIcon: React.FC<ItemIconProps> = ({
  icon,
  iconSheet,
  type,
  size = 24,
  className = '',
  title,
}) => {
  // A folha do acervo é uma grade de 16 × 20 ícones de 32 pixels. Medindo tudo
  // em múltiplos do lado pedido, a mesma conta serve para pixel e para `em`.
  const side = typeof size === 'number' ? `${size}px` : size;
  const COLUMNS = 16;
  const ROWS = 20;

  if (typeof icon === 'number' && icon >= 0) {
    const col = icon % COLUMNS;
    const row = Math.floor(icon / COLUMNS);

    return (
      <span
        className={`item-icon-sprite ${className}`}
        title={title}
        style={{
          display: 'inline-block',
          width: side,
          height: side,
          backgroundImage: `url(${ASSETS_BASE}/img/system/${iconSheet || 'IconSet'}.webp)`,
          backgroundSize: `calc(${side} * ${COLUMNS}) calc(${side} * ${ROWS})`,
          backgroundPosition: `calc(${side} * ${-col}) calc(${side} * ${-row})`,
          backgroundRepeat: 'no-repeat',
          imageRendering: 'pixelated',
          flexShrink: 0,
          verticalAlign: 'middle',
        }}
        aria-hidden="true"
      />
    );
  }

  const fallback = (type ? TYPE_FALLBACKS[type] : null) ?? '⚔️';
  return (
    <span
      className={`item-icon-fallback ${className}`}
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: side,
        height: side,
        fontSize: `calc(${side} * 0.75)`,
        lineHeight: 1,
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
      aria-hidden="true"
    >
      {fallback}
    </span>
  );
};
